import React, { useMemo, useState, useEffect } from "react";
import { AirCfarHtsMerchandising, useDeviceTypes } from "halifax";
import {
  CfarEntryPointVariantEnum,
  CfarOffer,
  VIEW_BOOKING_ADD_ON_POST_BOOKING,
} from "redmond";
import "./styles.scss";
import clsx from "clsx";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  getExperimentVariant,
  POST_BOOKING_CFAR_FARE_OPTIMIZATION_ENTRY_POINT,
  useExperiments,
} from "../../../../context/experiments";

export interface ICap1CfarPostBookingOfferMerchandisingProps {
  cfarOffer: CfarOffer | undefined;
  selectedAccountReferenceId: string | undefined;
  onSelect: () => void;
}

export const Cap1CfarPostBookingOfferMerchandising = ({
  cfarOffer,
  selectedAccountReferenceId,
  onSelect,
}: ICap1CfarPostBookingOfferMerchandisingProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const { matchesMobile } = useDeviceTypes();

  const expState = useExperiments();

  const postBookingCfarFoEntryPointExp = getExperimentVariant(
    expState.experiments,
    POST_BOOKING_CFAR_FARE_OPTIMIZATION_ENTRY_POINT,
  );

  const isPostBookingCfarFoEntryPointEnabled = useMemo(
    () => postBookingCfarFoEntryPointExp === AVAILABLE,
    [postBookingCfarFoEntryPointExp],
  );

  const { rewardsType, rewardsConversion } = useMemo(() => {
    if (!selectedAccountReferenceId) {
      return { rewardsType: undefined, rewardsConversion: undefined };
    }

    const rewardsPremiumAmount =
      cfarOffer?.premiumPerPax.rewards[selectedAccountReferenceId];

    if (
      !rewardsPremiumAmount ||
      rewardsPremiumAmount.value === 0 ||
      cfarOffer?.premiumPerPax.fiat.value === 0
    ) {
      return { rewardsType: undefined, rewardsConversion: undefined };
    }

    return {
      rewardsType: rewardsPremiumAmount.currency.toLowerCase(),
      rewardsConversion:
        rewardsPremiumAmount.value / cfarOffer!.premiumPerPax.fiat.value,
    };
  }, [cfarOffer, selectedAccountReferenceId]);

  const merchandisingUrl = useMemo(() => {
    if (!cfarOffer?.merchandisingUrl) {
      return null;
    }

    const url = new URL(cfarOffer.merchandisingUrl);

    if (rewardsType && rewardsConversion) {
      url.searchParams.set("rewardsType", rewardsType);
      url.searchParams.set("rewardsConversion", rewardsConversion.toString());
    }

    return url;
  }, [cfarOffer, rewardsType, rewardsConversion]);

  useEffect(() => {
    if (!merchandisingUrl || !cfarOffer) return;
    trackEvent({
      eventName: VIEW_BOOKING_ADD_ON_POST_BOOKING,
      properties: {
        is_cfar: true,
        cfar_per_pax: cfarOffer.premiumPerPax.fiat.value,
      },
    });
  }, []);

  if (!merchandisingUrl) {
    return null;
  }

  return (
    <div
      className={clsx("cfar-post-booking-offer-merchandising", {
        mobile: matchesMobile,
      })}
    >
      <AirCfarHtsMerchandising
        merchandisingUrl={merchandisingUrl}
        onSelect={onSelect}
        onLoadSuccess={() => setIsLoading(false)}
        onLoadError={() => setIsError(true)}
        isLoading={isLoading}
        isError={isError}
        offerId={cfarOffer!.id.productId}
        entryPoint={
          isPostBookingCfarFoEntryPointEnabled &&
          cfarOffer?.entryPointVariant?.CfarEntryPointVariant ===
            CfarEntryPointVariantEnum.PostBookingFo
            ? "postbooking-fo"
            : "postbooking"
        }
      />
    </div>
  );
};
